"use client";

import { Box, Button, TextField, Typography } from "@mui/material";
import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useEffect, useState, useTransition } from "react";
import BreadcrumbMenu from "@/components/core/breadcrumb-menu";
import ProductList from "@/components/core/product-list";
import CustomCheckbox from "@/components/core/mui-custom-components/checkbox-custom";
import TreeSelect from "./tree-select";
import AlertComponent from "@/components/core/alert";
import { useAlertStore } from "@/components/context/alert-store";
import { CollectionMeta } from "@/main/category/(components)/content";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

interface CheckboxData {
  isSearchInCategories: boolean;
  isSearchInDescription: boolean;
}

interface CProps {
  lang: string;
  products: any[];
  meta: CollectionMeta;
  categories: any[];
}

const SearchPage: React.FC<CProps> = ({ lang, products, meta, categories }) => {
  const searchParams = useSearchParams();
  const { i18n } = useLingui();

  const { message, setMessage } = useAlertStore();

  const [checkboxState, setCheckboxState] = useState<CheckboxData>({
    isSearchInCategories: searchParams.get("sc") === "1",
    isSearchInDescription: searchParams.get("sd") === "1",
  });

  const [searchKey, setSearchKey] = useState<string>(searchParams.get("s") || "");
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get("c") || "");
  const [errors, setErrors] = useState<boolean>(false);
  const [loading, startTransition] = useTransition();

  const pathname = usePathname();
  const { replace } = useRouter();

  const handleSearch = (key: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("s", key);

    params.set("page", "1");

    if (selectedCategory) {
      params.set("c", selectedCategory);
    } else {
      params.delete("c");
    }

    if (checkboxState.isSearchInDescription) {
      params.set("sd", "1");
    } else {
      params.delete("sd");
    }

    if (checkboxState.isSearchInCategories) {
      params.set("sc", "1");
    } else {
      params.delete("sc");
    }
    startTransition(() => {
      replace(`${pathname}?${params.toString()}`);
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setCheckboxState((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  const [showAlert, setShowAlert] = useState<boolean>(true);

  useEffect(() => {
    if (errors) {
      setShowAlert(false);
    }
  }, [errors]);

  return (
    <main className="container min-h-[80vh] pb-[50px] xs:pt-[75px] md:pt-5">
      {showAlert && <AlertComponent message={message} setMessage={setMessage} />}
      <BreadcrumbMenu layout="search" />
      <Typography sx={{ my: "15px", fontWeight: 500, fontSize: 20 }}>
        <Trans>Search</Trans>
        {searchKey && <span className="px-1">- {searchKey}</span>}
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: { xs: "10px", md: "20px" },
          alignItems: { xs: "flex-start", md: "flex-end" },
          mb: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: { xs: "100%", md: "40%" },
          }}
        >
          <Typography sx={{ fontSize: 13 }}>
            <Trans>Search criteria</Trans>
          </Typography>
          <TextField
            placeholder={t(i18n)`Keywords`}
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleSearch(searchKey);
              }
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: "20px",
            alignItems: { xs: "flex-start", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            width: { xs: "100%", md: "60%" },
          }}
        >
          <TreeSelect data={categories} onCategoryChange={setSelectedCategory} />
          <CustomCheckbox
            disabled={!selectedCategory}
            name="isSearchInCategories"
            labelComponents={t(i18n)`Search in subcategories`}
            checked={checkboxState.isSearchInCategories}
            onChange={handleCheckboxChange}
          />
        </Box>
      </Box>
      <CustomCheckbox
        name="isSearchInDescription"
        labelComponents={t(i18n)`Search in product descriptions`}
        checked={checkboxState.isSearchInDescription}
        onChange={handleCheckboxChange}
      />
      <Button
        variant="contained"
        sx={{ mt: 2, mb: "25px", height: "35px", borderRadius: "4px", fontWeight: 500 }}
        onClick={() => handleSearch(searchKey)}
      >
        <Trans>Search</Trans>
      </Button>
      <Typography sx={{ mb: "15px", fontSize: 20, fontWeight: 500 }}>
        <Trans>Products meeting the search criteria</Trans>
      </Typography>
      {products.length > 0 ? (
        <ProductList
          lang={lang}
          products={products}
          setErrors={setErrors}
          meta={meta}
          showLoader={loading}
        />
      ) : (
        <Typography variant="subtitle1">
          <Trans>There is no product that matches the search criteria.</Trans>
        </Typography>
      )}
    </main>
  );
};

export default SearchPage;
